import React, { useEffect, useState } from 'react';
import 'utilities/global.css';
import previewStyle from './preview.module.css';
import style from './style.module.css';
import SVG from 'react-inlinesvg';
import {
  ANIMATION_RUN,
  IRedirectWidgetCustomisation,
  WidgetSchema,
  LANGUAGE_VALUES,
  LANGUAGE_ISO_CODES,
} from '@bikdotai/bik-models/dm';
import useIsMobile from '../../hooks/useIsMobile';
import { LanguageUtils } from 'utilities/languageUtilities';

export interface IFab extends WidgetSchema {
  icon: any;
  onClick: () => void;
  preview?: boolean;
  isMobile?: boolean;
  buttonStyle?: React.CSSProperties;
  id?: string;
}

const AnimationElement: React.FC<{
  item: string;
  animationRun: ANIMATION_RUN;
  lottieName: string;
}> = ({ item, animationRun, lottieName }) => {
  const loopValue = {
    [ANIMATION_RUN.ONCE]: 0,
    [ANIMATION_RUN.THREE_TIMES]: 2,
    [ANIMATION_RUN.TEN_TIMES]: 9,
    [ANIMATION_RUN.IN_A_LOOP]: true,
  };
  useEffect(() => {
    if (item) {
      import('lottie-web/build/player/lottie_light').then(module => {
        const lottie = module.default;
        const containerDiv = document.getElementById(
          `lottie-container-${lottieName}`,
        );
        containerDiv.innerHTML = '';
        lottie.loadAnimation({
          container: containerDiv,
          path: item,
          renderer: 'svg',
          loop: loopValue[animationRun],
          autoplay: true,
        });
      });
    }
  }, [item, animationRun]);
  return <></>;
};

export const IconSvg: React.FC<{
  item: string;
  iconColor: string;
  size: number;
}> = ({ item, iconColor, size }) => {
  return (
    <SVG
      key={iconColor}
      src={item}
      width={size}
      height={size}
      preProcessor={code => code.replace(/fill=".*?"/g, `fill="${iconColor}"`)}
    />
  );
};

const Fab = (props: IFab) => {
  const widgetCustomisation =
    props.widgetCustomisation as IRedirectWidgetCustomisation;
  const languageUtils = new LanguageUtils();
  const [widgetLanguage, setWidgetLanguage] = useState(LANGUAGE_VALUES.ENGLISH);
  const { top, bottom, left, right } = widgetCustomisation?.fabPositioner ?? {};
  const transform = widgetCustomisation?.fabTransform;
  const [fabText, setFabText] = useState(
    widgetCustomisation?.multiLingualConfigurations?.[widgetLanguage]
      ?.fabText ?? widgetCustomisation?.fabText,
  );
  const fabIconLink = widgetCustomisation.fabIconLink;
  const fabImage = widgetCustomisation.fabImage;
  const fabLottie = widgetCustomisation.fabLottie;
  const isMobile = props.isMobile || screen.width < 768;
  const fabSize = isMobile
    ? widgetCustomisation?.fabMobileSize
    : widgetCustomisation?.fabWebSize;
  const fSize = fabSize ?? 75;
  let currentStyle;
  if (props?.preview) {
    currentStyle = previewStyle;
  } else {
    currentStyle = style;
  }

  const getElementsByIdPrefix = prefix => {
    let elements = document.querySelectorAll("[id^='" + prefix + "']");
    return Array.from(elements);
  };

  useEffect(() => {
    const isBikURL =
      window.location.href?.includes('https://dashboard.bik.ai/') ||
      window.location.href?.includes('https://staging.dashboard.bik.ai/');
    const previewLanguage = props?.widgetCustomisation?.previewLanguage;
    const storeLanguage =
      (isBikURL ? previewLanguage : window?.Shopify?.locale) ??
      previewLanguage ??
      LANGUAGE_ISO_CODES.EN;
    setWidgetLanguage(languageUtils.getStoreLanguage(storeLanguage));
  }, [window.location.href, props?.widgetCustomisation?.previewLanguage]);

  let lottieName;
  if (fabLottie) {
    const indexOfLastSlash = fabLottie.lastIndexOf('/') + 1;
    const indexOfJson = fabLottie.lastIndexOf('.json');
    lottieName = fabLottie.slice(indexOfLastSlash, indexOfJson);
  }

  useEffect(() => {
    if (!fabLottie && !fabImage && !fabIconLink) {
      const elements = getElementsByIdPrefix('lottie-container-');
      elements.forEach(element => {
        element.innerHTML = '';
      });
    }
  }, [fabLottie, fabImage, fabIconLink]);

  useEffect(() => {
    setFabText(
      widgetCustomisation?.multiLingualConfigurations?.[widgetLanguage]
        ?.fabText ?? widgetCustomisation?.fabText,
    );
  }, [
    widgetCustomisation?.fabText,
    widgetCustomisation?.multiLingualConfigurations,
    widgetLanguage,
  ]);
  let buttonStyle;
  if (fabText) {
    buttonStyle = props.buttonStyle ?? {
      width: 'fit-content',
      height: 'fit-content',
      borderRadius: 20,
    };
  }

  return (
    <>
      <button
        id={props.id}
        disabled={props.preview}
        className={currentStyle.container}
        style={{
          zIndex: 2147483647,
          cursor: 'pointer',
          border: 'none',
          transform: transform,
          backgroundColor: fabImage?.length
            ? undefined
            : `${widgetCustomisation?.fabBackgroundColor}`,
          top,
          left,
          bottom,
          right,
          boxShadow: fabImage
            ? undefined
            : 'rgba(0, 0, 0, 0.15) 0px 14px 28px,rgba(0, 0, 0, 0.15) 0px 10px 10px',
          position: widgetCustomisation?.fabPositioner,
          height: fSize,
          width: fSize,
          borderRadius: '50%',
          ...buttonStyle,
        }}
        onClick={props.onClick}
      >
        <div
          className={currentStyle.bikFabWrapper}
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            padding: fabText ? 8 : undefined,
          }}
        >
          {fabIconLink && !fabImage && (
            <IconSvg
              item={fabIconLink}
              size={fSize - 30}
              iconColor={widgetCustomisation.iconColor}
            />
          )}
          {fabImage && (
            <img
              width={fSize}
              height={fSize}
              src={fabImage}
              alt={'Fab image'}
            />
          )}
          <div
            id={`lottie-container-${lottieName}`}
            style={{ display: fabLottie ? 'block' : 'none' }}
          >
            {fabLottie && (
              <AnimationElement
                item={fabLottie}
                animationRun={widgetCustomisation.fabAnimationRun}
                lottieName={lottieName}
              />
            )}
          </div>
          {fabText && <div className={currentStyle.fabText}>{fabText}</div>}
        </div>
      </button>
    </>
  );
};

export default Fab;
